import { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import Label from '../../../components/Label';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table columns ──────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'deducteeName', label: 'Deductee Name' },
  { id: 'pan', label: 'PAN' },
  { id: 'section', label: 'Section' },
  { id: 'period', label: 'Period' },
  { id: 'paymentAmt', label: 'Payment Amt' },
  { id: 'tdsRate', label: 'TDS Rate' },
  { id: 'tdsAmt', label: 'TDS Amount' },
  { id: 'tdsDeposited', label: 'TDS Deposited' },
  { id: 'status', label: 'Status' }
];

// ── Mock Deductee Data (replace with API) ──────────────────────────────
const MOCK_DEDUCTEES = [
  {
    id: 1,
    deducteeName: 'John Doe (Consultant)',
    pan: 'ABCDE1234F',
    section: '194J',
    period: 'Apr 2024',
    paymentAmt: 50000,
    tdsRate: '10%',
    tdsAmt: 5000,
    tdsDeposited: 5000,
    status: 'Deposited'
  },
  {
    id: 2,
    deducteeName: 'Secure Guards Ltd',
    pan: 'FGHIJ5678G',
    section: '194C',
    period: 'Apr 2024',
    paymentAmt: 25000,
    tdsRate: '2%',
    tdsAmt: 500,
    tdsDeposited: 500,
    status: 'Deposited'
  },
  {
    id: 3,
    deducteeName: 'TechPro Solutions Pvt Ltd',
    pan: 'KLMNO9012H',
    section: '194J',
    period: 'May 2024',
    paymentAmt: 100000,
    tdsRate: '10%',
    tdsAmt: 10000,
    tdsDeposited: 10000,
    status: 'Deposited'
  },
  {
    id: 4,
    deducteeName: 'Raj Kumar (Professional)',
    pan: 'PQRST3456I',
    section: '194J',
    period: 'Jun 2024',
    paymentAmt: 75000,
    tdsRate: '10%',
    tdsAmt: 7500,
    tdsDeposited: 0,
    status: 'Pending'
  },
  {
    id: 5,
    deducteeName: 'Global Freight Pvt Ltd',
    pan: 'UVWXY7890J',
    section: '194C',
    period: 'Jun 2024',
    paymentAmt: 40000,
    tdsRate: '2%',
    tdsAmt: 800,
    tdsDeposited: 0,
    status: 'Pending'
  },
  {
    id: 6,
    deducteeName: 'Alpha Commission Agency',
    pan: 'ABCDE2345K',
    section: '194H',
    period: 'Jul 2024',
    paymentAmt: 30000,
    tdsRate: '5%',
    tdsAmt: 1500,
    tdsDeposited: 0,
    status: 'Pending'
  }
];

const SECTIONS = ['194C', '194J', '194H', '194I', '194A'];
const STATUS_OPTIONS = ['Deposited', 'Pending'];

const statusColor = (s) =>
  ({ Deposited: 'success', Pending: 'warning', Overdue: 'error' }[s] || 'warning');

export default function TDSDeducteeDetails() {
  const [filterName, setFilterName] = useState('');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [filterSection, setFilterSection] = useState(' ');
  const [filterStatus, setFilterStatus] = useState(' ');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('deducteeName');
  const [exportAnchor, setExportAnchor] = useState(null);

  const handleRequestSort = (_, property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = useMemo(() => {
    let data = [...MOCK_DEDUCTEES];
    if (filterName)
      data = data.filter(
        (r) =>
          r.deducteeName.toLowerCase().includes(filterName.toLowerCase()) ||
          r.pan.toLowerCase().includes(filterName.toLowerCase())
      );
    if (filterSection !== ' ') data = data.filter((r) => r.section === filterSection);
    if (filterStatus !== ' ') data = data.filter((r) => r.status === filterStatus);
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, filterSection, filterStatus, order, orderBy]);

  const handleExport = (type) => {
    setExportAnchor(null);
    console.log('TDS Deductee export as', type);
  };

  return (
    <Box>
      <Card>
        {/* ── Collapsible Filter Bar ── */}
        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: 12 }}>TDS Section</InputLabel>
                  <Select
                    label="TDS Section"
                    value={filterSection}
                    onChange={(e) => setFilterSection(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Sections</MenuItem>
                    {SECTIONS.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: 12 }}>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Status</MenuItem>
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs="auto">
                <Tooltip title="Reset Filters">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFilterSection(' ');
                      setFilterStatus(' ');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          {/* LEFT — Export icon */}
          <Box>
            <Tooltip title="Export data" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                <MdOutlineSystemUpdateAlt size={18} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>
          </Box>

          {/* RIGHT — Search + Filter */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search name, PAN…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filters">
              <IconButton onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 900, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={filtered.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filtered.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell sx={{ fontWeight: 600 }}>{row.deducteeName}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: 11, color: '#0d4689', fontWeight: 600 }}>
                      {row.pan}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={row.section}
                        size="small"
                        sx={{ fontSize: 10, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{row.period}</StyledTableCell>
                    <StyledTableCell>₹ {row.paymentAmt.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>{row.tdsRate}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600 }}>
                      ₹ {row.tdsAmt.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.tdsDeposited > 0 ? (
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>
                          ₹ {row.tdsDeposited.toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>–</span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Label variant="ghost" color={statusColor(row.status)}>
                        {row.status}
                      </Label>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {filtered.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={9} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>
    </Box>
  );
}
